// ---------- fine shapes alphabet ----------
// Same language as `motifs`, with two more coverage levels: 0 empty · 1/4 · 1/2 · 3/4 · 1
// full. The half level keeps the 8-way orientation (cardinal + diagonal); 1/4 and 3/4 only
// orient on the 4 cardinal axes — a diagonal sliver at those thinner/thicker fractions
// would be hard to read, and the two new levels exist to soften banding, not to add
// direction precision the half level doesn't already carry.
//
//   0 empty · 1 full
//   2..9    half, same codes and geometry as `motifs` (bottom/top/left/right/4 diagonals)
//   10..13  quarter, cardinal only (bottom/top/left/right)
//   14..17  three-quarter, cardinal only (bottom/top/left/right)

import { inkColor } from '../ink';
import { shapePath } from './motifs';
import type { Alphabet } from './types';

// clockwise cardinal sectors from the right -> shape. Exported for motifsFin.encode.ts,
// which needs the same order to place the quarter tiers; the 8-way table is motifs' own
// SECTOR, imported rather than restated.
export const SECTOR4 = [5, 2, 4, 3];

// code (10..17) -> { shape, frac }, in the same cardinal order as SECTOR4
const QUARTER = SECTOR4.map((shape) => ({ shape, frac: 0.25 }));
const THREE_QUARTER = SECTOR4.map((shape) => ({ shape, frac: 0.75 }));

function shapeOf(code: number): { shape: number; frac: number } {
  if (code >= 10 && code <= 13) return QUARTER[code - 10];
  return THREE_QUARTER[code - 14]; // 14..17
}

export const motifsFin: Alphabet = {
  id: 'motifsFin',
  sheetMode: 'checker',
  label: 'Motifs fins',
  symbolName: 'motif',
  helpText:
    'Comme Motifs, avec deux paliers de plus : <b>1/4</b> et <b>3/4</b> de remplissage en ' +
    'plus du vide, de la moitié et du plein — un dégradé plus doux, au prix de plus de ' +
    "formes à reconnaître. Le 1/2 s'oriente sur 8 directions, le 1/4 et le 3/4 sur 4 " +
    'seulement (pas de diagonales).',

  aspect: () => 1,

  // Same reading order as motifs (least revealing first, full cell last), the two new
  // tiers framing the half level on either side of its coverage.
  paletteOrder: [10, 11, 12, 13, 2, 3, 4, 5, 6, 7, 8, 9, 14, 15, 16, 17, 1],

  draw(ctx, x, y, w, h, code) {
    if (!code) return;
    ctx.fillStyle = inkColor();
    const s = Math.min(w, h);
    if (code < 10) {
      shapePath(ctx, x, y, s, code);
    } else {
      const { shape, frac } = shapeOf(code);
      shapePath(ctx, x, y, s, shape, frac);
    }
    ctx.fill();
  },

  // a shape glued to the swatch's edges is illegible: it needs breathing room
  swatch(ctx, w, h, code) {
    motifsFin.draw(ctx, 3, 3, Math.min(w, h) - 6, Math.min(w, h) - 6, code);
  },
};
